import {DateTime} from 'luxon'
import {dateFormats} from '@/constants/date-and-time.js'
import {myEnrollmentsApi} from '@/api/my-enrollments.api.js'
import {useFlashMessages} from '@/composables/use-flash-messages.js'
import {ref, watch} from 'vue'

/**
 * Enrollment editing composable
 */
export function useEnrollmentEditing(enrollment) {

  //Flash messages
  const {setFlashSuccess, setFlashFailure} = useFlashMessages()

  //Model
  const model = ref({
    timezone: '',
    enrollmentTime: null,
  })

  //Update model
  const hasMadeChanges = ref(false)
  const updateModel = data => {
    Object.assign(model.value, data)
    hasMadeChanges.value = true
  }

  //Enrollment update
  const isUpdatingEnrollment = ref(false)
  const updateEnrollment = async () => {

    //Must have enrollment
    if (!enrollment.value?.id) {
      throw new Error(`Must have enrollment reference to an existing enrollment`)
    }

    //No changes made to model
    if (!hasMadeChanges.value) {
      return
    }

    //Toggle flag
    isUpdatingEnrollment.value = true

    //Extract data for update
    const {id} = enrollment.value
    const data = {...model.value}

    //Update
    try {
      const json = await myEnrollmentsApi.updateById(id, data)
      enrollment.value = json.enrollment
      hasMadeChanges.value = false
      setFlashSuccess('Enrollment updated')
    }
    catch (error) {
      setFlashFailure('Failed to update enrollment')
      throw error
    }
    finally {
      isUpdatingEnrollment.value = false
    }
  }

  //Watch enrollment for changes and update initial model value accordingly
  watch(enrollment, () => {
    if (enrollment.value && model.value) {
      model.value.timezone = enrollment.value.effectiveTimezone
      model.value.enrollmentTime = DateTime
        .fromISO(enrollment.value.expectedDeliveryTime)
        .setZone(enrollment.value.timezone)
        .toFormat(dateFormats.TIME)
    }
  }, {immediate: true})

  //Expose interface
  return {
    model,
    updateModel,
    updateEnrollment,
    isUpdatingEnrollment,
  }
}
